import { MeasurementUnitEnum } from 'domains/measurementDomain';
import { RevxJobSize } from 'components/RevxJobSize';
import { Divider } from 'antd';
import classnames from 'classnames';
import RevxFlexLayout from 'components/RevxFlexLayout';
import { RevxMoney } from 'components/RevxMoney';
import { JobSpec } from 'domains/jobconfigDomain';
import { OrientationEnum, SKU } from 'domains/skuDomain';
import {
  JobAdjustmentPrintingPartViewer,
  JobAdjustmentTotalViewer,
  JobItem,
  PaperOrientationEnum,
} from 'domains/workspaceDomain';
import {
  getJobSpecPrintingBackStringForStep,
  getJobSpecPrintingFrontStringForStep,
} from 'utils/jobConfig';
import { formatNumberToThousandths } from 'utils/number';

type Props = {
  jobName: number;
  quantity: number;
  size: string;
  /** Metric twin of `size`, so a unit switch needs no refetch. */
  sizeMetric?: string;
  /** The ready-made size this job was configured with; shown only in its own unit. */
  sizePreset?: string;
  sizePresetUnit?: MeasurementUnitEnum;
  services: string[];
  orientation: PaperOrientationEnum;
  adjustTotal: JobAdjustmentTotalViewer;
  parts: JobAdjustmentPrintingPartViewer[];
  jobData?: JobItem;
};

const JobAdjustmentPrinting = function ({
  jobName,
  quantity,
  size,
  sizeMetric,
  sizePreset,
  sizePresetUnit,
  services,
  orientation,
  adjustTotal,
  parts,
  jobData,
}: Props) {
  const firstPart = parts?.[0];
  const isMultiple = parts.length > 1;

  return (
    <>
      <RevxFlexLayout
        className="title-bar"
        justify="space-between"
        align="center"
      >
        <div className="title-bar__name">
          <span>Printing</span>
        </div>
        <div className="title-bar__price">
          <RevxMoney value={adjustTotal.printingCost} />
        </div>
      </RevxFlexLayout>
      <div
        className={classnames('base-info', {
          'is-single': !isMultiple,
        })}
      >
        <div className="base-info__row bold normal">
          <span>{jobName}</span>
          <Divider type="vertical" />
          <span>QTY: </span>
          <span>{formatNumberToThousandths(quantity || 0)}</span>
        </div>
        {firstPart && (
          <div className="base-info__row">
            <span className="bold">Paper: </span>
            <span>{firstPart.paperName}</span>
          </div>
        )}
        <div className="base-info__row normal">
          <span className="bold">Size: </span>
          <span>
            <RevxJobSize
              isWorkspace
              size={size}
              sizeMetric={sizeMetric}
              sizePreset={sizePreset}
              sizePresetUnit={sizePresetUnit}
              orientation={orientation}
            />
          </span>
        </div>
        {firstPart && (
          <div className="base-info__row">
            <span className="bold">Press: </span>
            <span>{firstPart.pressName}</span>
            <Divider type="vertical" />
            <span className="bold">Color: </span>
            <span>
              {firstPart.inkBack && firstPart.inkBack === firstPart.inkFront ? (
                <>{`Both sides in color (${firstPart.inkBack})`}</>
              ) : (
                <>
                  {getJobSpecPrintingFrontStringForStep(
                    {
                      pmsFront: firstPart.pmsFront,
                      coatingFront: firstPart.coatingFront,
                      inkFront: firstPart.inkFront,
                    } as JobSpec,
                    {
                      name: firstPart.skuName,
                      skuType: {
                        name: firstPart.skuTypeName,
                      },
                    } as SKU
                  )}
                  <Divider type="vertical" />
                  {getJobSpecPrintingBackStringForStep(
                    {
                      pmsBack: firstPart.pmsBack,
                      coatingBack: firstPart.coatingBack,
                      inkBack: firstPart.inkBack,
                    } as JobSpec,
                    {
                      name: firstPart.skuName,
                      skuType: {
                        name: firstPart.skuTypeName,
                      },
                    } as SKU
                  )}
                </>
              )}
            </span>
          </div>
        )}
        <div
          className="base-info__row normal"
          style={{
            visibility:
              (orientation.toLocaleLowerCase() as OrientationEnum) ===
              OrientationEnum.cube
                ? 'hidden'
                : 'visible',
          }}
        >
          <span className="bold">Orientation: </span>
          <span style={{ textTransform: 'capitalize' }}>{orientation}</span>
        </div>
        {(jobData?.digitalProofRequired || jobData?.hardcopyProofRequired) &&
        !services.length ? (
          <div className="base-info__row"></div>
        ) : (
          <div className="base-info__row normal">
            <span className="bold">Services: </span>
            {!!services.length && (
              <>
                {' '}
                {services.map((service, index) => (
                  <span key={service}>
                    <span>{service}</span>
                    {index !== services.length - 1 ? (
                      <Divider type="vertical" />
                    ) : (
                      <>{` `}</>
                    )}
                  </span>
                ))}
              </>
            )}
            {!jobData?.digitalProofRequired &&
              !jobData?.hardcopyProofRequired && (
                <span>
                  {!!services.length && <Divider type="vertical" />}PDF Proof -
                  None
                </span>
              )}
          </div>
        )}
        {isMultiple &&
          parts.slice(1).map((item, index) => {
            return (
              <div
                key={item.paperName}
                style={{
                  width: '100%',
                  display: 'flex',
                  flexWrap: 'wrap',
                  columnGap: '40px',
                }}
              >
                <div className="base-info__row title" style={{ width: '100%' }}>
                  {item.skuName}
                </div>
                <div className="base-info__row">
                  <span className="bold">Paper: </span>
                  <span>{item.paperName}</span>
                </div>
                <div className="base-info__row">
                  <span className="bold">Press: </span>
                  <span>{item.pressName}</span>
                  <Divider type="vertical" />
                  <span className="bold">Color: </span>
                  <span>
                    {item.inkBack && item.inkBack === item.inkFront ? (
                      <>{`Both sides in color (${item.inkBack})`}</>
                    ) : (
                      <>
                        {getJobSpecPrintingFrontStringForStep(
                          {
                            pmsFront: item.pmsFront,
                            coatingFront: item.coatingFront,
                            inkFront: item.inkFront,
                          } as JobSpec,
                          {
                            name: item.skuName,
                            skuType: {
                              name: item.skuTypeName,
                            },
                          } as SKU
                        )}
                        <Divider type="vertical" />
                        {getJobSpecPrintingBackStringForStep(
                          {
                            pmsBack: item.pmsBack,
                            coatingBack: item.coatingBack,
                            inkBack: item.inkBack,
                          } as JobSpec,
                          {
                            name: item.skuName,
                            skuType: {
                              name: item.skuTypeName,
                            },
                          } as SKU
                        )}
                      </>
                    )}
                  </span>
                </div>
              </div>
            );
          })}
      </div>
    </>
  );
};

export default JobAdjustmentPrinting;
